import fp from 'lodash/fp';
import { Dispatch, Reducer } from 'redux';
import { Common } from '@factorin/domain';

import { ActionPayload, Action, ActionReducer, ActionLookupReducer, StatePart } from '../types';


type Actions = {
    [action: string]: Action;
}

export const prefixTypes = <TTypes extends {}>(
    prefix: string,
    types: TTypes,
): TTypes => fp.mapValues(type => `${prefix}/${type}`, types) as TTypes;

export type ActionCreator<TPayload = any> =
    ($payload: TPayload) => { type: string, $payload?: TPayload };

export const createAction = <TPayload>(type: string): ActionCreator<TPayload> => Object.assign(
    <TPayload>($payload: TPayload) => ({ type, $payload }),
    {
        toString: () => type,
    },
);

type ActionWithPath<TPayload> = { type: string, path: string, $payload: TPayload };

export type ActionCreatorWithPath<TPayload = any> = {
    (path: string): ($payload: TPayload) => ActionWithPath<TPayload>;
    (path: string, $payload: TPayload): ActionWithPath<TPayload>;
};

export const createActionWithPath =
    <TPayload = any>(type: string) => <ActionCreatorWithPath<TPayload>>(Object.assign(
        <TPayload>(path: string, $payload?: TPayload) => {
            if ($payload)
                return { type, path, $payload };
            return <TPayload>($payload: TPayload): ActionWithPath<TPayload> =>
                ({ type, path, $payload });
        },
        {
            toString: () => type,
        },
    ));

export const lookupTableReducer = (
    defaultState: any,
    lookupTable: ActionLookupReducer,
): Reducer<any, ActionPayload> =>
    (state = defaultState, { type, $payload, ...props }: ActionPayload) =>
        type in lookupTable
            ? lookupTable[type](state, $payload, props)
            : state;

export const reducePath = <TState = any>(reducer: ActionReducer<TState>): ActionReducer<TState> =>
    (state, $payload, { path, ...props }) => ({
        ...state,
        [path]: reducer(state[path], $payload, props),
    });

export const applyPath = (path: string, actions: Action[]) =>
    fp.mapValues(action => action(path), actions);

export const bindPathActions = (
    pathActions: Actions,
    actions: Actions = {},
) =>
    (dispatch: Dispatch, { path }: { path: string }) => ({
        ...fp.mapValues(
            action => (...args: any[]) => dispatch(action(path, ...args)),
            pathActions,
        ),
        ...fp.mapValues(
            action => (...args: any[]) => dispatch(action(...args)),
            actions,
        ),
    });

const mapReducerParts = fp.pipe(
    fp.mapValues('reducer'),
    fp.pickBy(fp.identity),
);

const mapSagasParts = fp.pipe(
    fp.mapValues('sagas'),
    fp.values,
    fp.filter(fp.identity),
    fp.flatten,
);

export const mergeStateParts = (prefix: string, parts: Common.Catalog<StatePart>) => ({
    reducer: {
        [prefix]: mapReducerParts(parts),
    },
    sagas: mapSagasParts(parts),
});

export const joinStateParts = (parts: Common.Catalog<StatePart>) => ({
    reducer: mapReducerParts(parts),
    sagas: mapSagasParts(parts),
});

const createPrefixed = <TPrefixes extends readonly string[], TActionCreator>(
    type: string,
    prefixes: TPrefixes,
    actionCreator: (type: string) => TActionCreator,
): Record<TPrefixes[number], TActionCreator> => prefixes.reduce(
    (actions, prefix) => {
        actions[prefix as TPrefixes[number]] = actionCreator(`${type}.${prefix}`);
        return actions;
    },
    {} as Record<TPrefixes[number], TActionCreator>,
);

export const createActionNamespace = (namespace: string) => Object.assign(
    <TPayload = any>(type: string) => createAction<TPayload>(`${namespace}/${type}`),
    {
        withPrefixes: <TPrefixes extends readonly string[]>(...prefixes: TPrefixes) =>
            <TPayload = any>(type: string) => createPrefixed<TPrefixes, ActionCreator<TPayload>>(
                `${namespace}/${type}`, prefixes, createAction,
            ),
        withPath: Object.assign(
            <TPayload = any>(type: string) =>
                createActionWithPath<TPayload>(`${namespace}/${type}`),
            {
                withPrefixes: <TPrefixes extends readonly string[]>(...prefixes: TPrefixes) =>
                    <TPayload = any>(type: string) =>
                        createPrefixed<TPrefixes, ActionCreatorWithPath<TPayload>>(
                            `${namespace}/${type}`, prefixes, createActionWithPath,
                        ),
            },
        ),
    },
);
